import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AuthContext } from "@/App";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Building2,
  MapPin,
  IndianRupee,
  ExternalLink,
  Bookmark,
  LogIn,
  Minimize2,
  Sparkles
} from "lucide-react";

export default function ChatWidget() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [matchedListings, setMatchedListings] = useState([]);
  const [showRequirementPrompt, setShowRequirementPrompt] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ name: "", mobile: "" });
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestInfoCollected, setGuestInfoCollected] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem("rebusi_chat_session");
    if (savedSession) {
      const session = JSON.parse(savedSession);
      setSessionId(session.sessionId);
      setMessages(session.messages || []);
      setPreferences(session.preferences || {});
      if (session.guestInfo) {
        setGuestInfo(session.guestInfo);
        setGuestInfoCollected(true);
      }
    }
  }, []);

  // Save session to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem("rebusi_chat_session", JSON.stringify({
        sessionId,
        messages,
        preferences,
        guestInfo: guestInfoCollected ? guestInfo : null
      }));
    }
  }, [sessionId, messages, preferences, guestInfo, guestInfoCollected]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && !showGuestForm) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized, showGuestForm]);

  // Show guest form or greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // If logged in user, show greeting directly
      if (user) {
        const greeting = {
          role: "assistant",
          content: `Hello ${user.name.split(' ')[0]}! I'm ReBusi Assistant. I can help you:\n\n• **Find businesses** matching your criteria\n• **Answer questions** about buying/selling businesses\n• **Guide you** through valuations and due diligence\n\nAre you looking to buy a business, or do you have questions about the platform?`,
          timestamp: new Date().toISOString()
        };
        setMessages([greeting]);
      } else if (!guestInfoCollected) {
        // Show guest form for non-logged in users
        setShowGuestForm(true);
      } else {
        // Guest info already collected, show personalized greeting
        const greeting = {
          role: "assistant",
          content: `Welcome back, ${guestInfo.name.split(' ')[0]}! I'm ReBusi Assistant. How can I help you today?\n\n• **Find businesses** matching your criteria\n• **Answer questions** about buying/selling businesses\n• **Guide you** through valuations and due diligence`,
          timestamp: new Date().toISOString()
        };
        setMessages([greeting]);
      }
    }
  }, [isOpen, user, guestInfoCollected]);

  const handleGuestFormSubmit = (e) => {
    e.preventDefault();
    if (!guestInfo.name.trim() || !guestInfo.mobile.trim()) {
      toast.error("Please enter your name and mobile number");
      return;
    }
    
    // Validate mobile number (basic validation)
    const mobileRegex = /^[+]?[\d\s-]{10,15}$/;
    if (!mobileRegex.test(guestInfo.mobile.replace(/\s/g, ''))) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    setGuestInfoCollected(true);
    setShowGuestForm(false);
    
    // Show personalized greeting
    const greeting = {
      role: "assistant",
      content: `Nice to meet you, ${guestInfo.name.split(' ')[0]}! I'm ReBusi Assistant. I can help you:\n\n• **Find businesses** matching your criteria\n• **Answer questions** about buying/selling businesses\n• **Guide you** through valuations and due diligence\n\nAre you looking to buy a business, or do you have questions about the platform?`,
      timestamp: new Date().toISOString()
    };
    setMessages([greeting]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await api.post("/chat/message", {
        message: userMessage.content,
        session_id: sessionId,
        preferences: preferences,
        guest_info: !user && guestInfoCollected ? guestInfo : null
      });

      setSessionId(response.data.session_id);
      
      const assistantMessage = {
        role: "assistant",
        content: response.data.message,
        timestamp: new Date().toISOString(),
        matchedListings: response.data.matched_listings,
        searchReady: response.data.search_ready
      };

      setMessages(prev => [...prev, assistantMessage]);
      setPreferences(response.data.preferences || {});

      if (response.data.matched_listings?.length > 0) {
        setMatchedListings(response.data.matched_listings);
        // Show requirement prompt if logged in
        if (user) {
          setShowRequirementPrompt(true);
        } else if (response.data.requires_login) {
          // Show login prompt
          const loginPrompt = {
            role: "assistant",
            content: "To save your preferences and get notified about matching businesses, please sign in to your account.",
            timestamp: new Date().toISOString(),
            isLoginPrompt: true
          };
          setMessages(prev => [...prev, loginPrompt]);
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = {
        role: "assistant",
        content: "I apologize, I'm having trouble responding right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateRequirement = async () => {
    if (!user) {
      toast.error("Please sign in to save your requirement");
      return;
    }

    try {
      await api.post(`/chat/create-requirement?session_id=${sessionId}`);
      toast.success("Buyer requirement created! Matching sellers will be notified.");
      setShowRequirementPrompt(false);
      
      const confirmMessage = {
        role: "assistant",
        content: "I've saved your requirement. Sellers with matching businesses will be notified. You can view and manage your requirements in your Dashboard.",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, confirmMessage]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create requirement");
    }
  };

  const handleViewInMarketplace = () => {
    const params = new URLSearchParams();
    if (preferences.industry) params.set("industry", preferences.industry);
    if (preferences.location) params.set("location", preferences.location);
    navigate(`/marketplace?${params.toString()}`);
    setIsOpen(false);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(1)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(1)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setPreferences({});
    setMatchedListings([]);
    setShowRequirementPrompt(false);
    setGuestInfo({ name: "", mobile: "" });
    setGuestInfoCollected(false);
    localStorage.removeItem("rebusi_chat_session");
    
    // Show guest form or greeting based on login status
    if (user) {
      const greeting = {
        role: "assistant",
        content: `Hello ${user.name.split(' ')[0]}! I'm ReBusi Assistant. How can I help you today?`,
        timestamp: new Date().toISOString()
      };
      setMessages([greeting]);
    } else {
      setShowGuestForm(true);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-[9999] w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-105"
        data-testid="chat-widget-trigger"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-10 right-0 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
          ReBusi Assistant
        </span>
        <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-20 right-6 z-[9999] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
        isMinimized ? "w-72 h-14" : "w-96 h-[550px]"
      }`}
      data-testid="chat-widget"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">ReBusi Assistant</h3>
            {!isMinimized && (
              <p className="text-xs text-slate-400">Online • Ready to help</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="chat-minimize-btn"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="chat-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Guest Info Form */}
          {showGuestForm && !user && (
            <div className="p-4 bg-gradient-to-br from-slate-50 to-orange-50">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Bot className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="font-semibold text-slate-900">Welcome to ReBusi!</h3>
                <p className="text-sm text-slate-600 mt-1">Please introduce yourself so I can assist you better</p>
              </div>
              <form onSubmit={handleGuestFormSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Your Name *</label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={guestInfo.name}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                    className="border-slate-200 focus:border-orange-400 focus:ring-orange-400"
                    data-testid="guest-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Mobile Number *</label>
                  <Input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={guestInfo.mobile}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, mobile: e.target.value }))}
                    className="border-slate-200 focus:border-orange-400 focus:ring-orange-400"
                    data-testid="guest-mobile-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="guest-form-submit"
                >
                  Start Chatting
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Already have an account?{" "}
                  <button 
                    type="button"
                    onClick={() => navigate("/auth")}
                    className="text-orange-500 hover:text-orange-600 font-medium"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            </div>
          )}

          {/* Messages Area */}
          {!showGuestForm && (
            <ScrollArea className="h-[400px] p-4">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] ${
                      msg.role === "user"
                        ? "bg-orange-500 text-white rounded-2xl rounded-br-md"
                        : "bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md"
                    } px-4 py-2.5 ${msg.isError ? "bg-red-50 text-red-700" : ""}`}
                  >
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.content.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </p>
                    
                    {/* Matched Listings */}
                    {msg.matchedListings?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Found {msg.matchedListings.length} matching businesses:
                        </p>
                        {msg.matchedListings.map((listing) => (
                          <div
                            key={listing.id}
                            className="bg-white rounded-lg p-3 border border-slate-200 hover:border-orange-300 transition-colors cursor-pointer"
                            onClick={() => navigate(`/listing/${listing.id}`)}
                          >
                            <div className="flex justify-between items-start">
                              <h4 className="font-medium text-sm text-slate-900 line-clamp-1">{listing.name}</h4>
                              <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" />
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {listing.industry}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {listing.location}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center justify-between">
                              <span className="text-orange-600 font-semibold text-sm">
                                {formatPrice(listing.asking_price)}
                              </span>
                              <span className="text-xs text-slate-500">
                                Rev: {formatPrice(listing.revenue)}/yr
                              </span>
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={handleViewInMarketplace}
                          className="w-full text-center text-xs text-orange-600 hover:text-orange-700 font-medium py-2"
                        >
                          View all in Marketplace →
                        </button>
                      </div>
                    )}

                    {/* Login Prompt */}
                    {msg.isLoginPrompt && (
                      <button
                        onClick={() => navigate("/auth")}
                        className="mt-3 w-full flex items-center justify-center gap-2 bg-orange-500 text-white text-sm py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        Sign In
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                      <span className="text-sm text-slate-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Requirement Prompt */}
              {showRequirementPrompt && user && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-sm text-slate-700 mb-3 flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-orange-500" />
                    Want me to save this as a Buyer Requirement?
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    Matching sellers will be notified about your requirements.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateRequirement}
                      className="flex-1 bg-orange-500 text-white text-sm py-2 px-3 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      Yes, save it
                    </button>
                    <button
                      onClick={() => setShowRequirementPrompt(false)}
                      className="flex-1 bg-slate-200 text-slate-700 text-sm py-2 px-3 rounded-lg hover:bg-slate-300 transition-colors"
                    >
                      No, thanks
                    </button>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
          )}

          {/* Input Area - Only show when not in guest form */}
          {!showGuestForm && (
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 border-slate-200 focus:border-orange-400 focus:ring-orange-400"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-orange-500 hover:bg-orange-600 px-3"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex justify-between items-center mt-2">
              <button
                onClick={clearChat}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Clear conversation
              </button>
              <span className="text-xs text-slate-400">
                Powered by AI
              </span>
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}
